/**
 * L'orbite du tandem : la règle pure de la bascule Concentré ↔ Dialogue (Wajih, 20/10).
 *
 * Les deux visages du tandem — Sam et le masteur — tournent autour de leur centre commun. Celui de
 * derrière passe devant : il grandit et le voile s'en va ; celui de devant recule, rapetisse, et le
 * voile vient. **L'échange se fait à mi-course** : celui qui avance se peint devant dès qu'il a fait
 * la moitié du chemin, pas au départ (avant, celui qui reculait passait devant d'un coup). À l'arrivée,
 * tout est à plat : une place, une échelle, un voile, ni perspective ni image écrasée.
 *
 * **Un seul déclencheur, le relais** (`lead`) : il change quand le mode change, ou quand le masteur
 * arrive alors que Sam menait l'attente. Un onglet changé à la main ne fait rien tourner. Quand la visio
 * entre ou sort avec le relais, l'orbite passe d'abord, puis la visio sort de la bulle ou y rentre
 * (`pages/StudentLivePage/stageMorph`).
 *
 * La course se compte en demi-tours (`turn`) : un nombre pair, Sam devant ; impair, l'autre devant.
 * Chaque relais avance d'un demi-tour, toujours dans le même sens ; un relais qui revient avant la fin
 * fait rebrousser chemin. Ramenée à [0, 2) (`orbitPhase`), la course dit où est chaque visage
 * (`orbitTrack`) : c'est ce que le crochet `useTandemOrbit` échantillonne pour `Animated`.
 */

export type OrbitPoint = { x: number; y: number }

/** Une place d'un visage : son centre, et son échelle (1 : sa taille posée). */
export type OrbitSlot = { center: OrbitPoint; scale: number }

/** Les deux places d'un visage : devant, derrière. Il est posé (sans transformation) à sa place de devant. */
export type OrbitFace = { front: OrbitSlot; back: OrbitSlot }

/** Où est un visage : son décalage depuis sa place de devant, son échelle, son recul (0 devant, 1 derrière). */
export type OrbitPose = { x: number; y: number; scale: number; back: number }

/** La course d'un relais : d'où elle part, où elle s'arrête (en demi-tours). */
export type OrbitCourse = { from: number; to: number }

/** Les points d'échantillonnage d'un demi-tour : assez pour que l'arc se lise comme un arc. */
export const ORBIT_STEPS = 12

/** Une courbe de Bézier cubique : les quatre nombres de `cubic-bezier`. */
export type OrbitCurve = readonly [number, number, number, number]

/**
 * La course vers Sam devant (le passage en Concentré, Sam envoyé, l'attente) : la vidéo du masteur qui
 * rétrécit dans son rond doit se voir (Wajih, daily du 23/10 : « on ne voit pas la visio rétrécir »). Sur la
 * courbe de la marque, la course fait les trois quarts de son chemin dans son premier quart de temps : la
 * vidéo saute dans son rond. Celle-ci n'en fait qu'un cinquième au premier quart, la moitié à 38 % du temps,
 * les trois quarts à la moitié, et se pose comme la marque. Sa durée : `orbitDurationMs`. Le retour vers le
 * masteur garde la courbe de la marque (Wajih : « très réussi »).
 */
export const ORBIT_FORWARD_CURVE: OrbitCurve = [0.45, 0, 0.2, 1]

/**
 * La course vers Sam devant, dans la visio : la vidéo du masteur s'y réduit vers son visage rond, à côté de
 * Sam, et l'élève doit la voir se réduire (Wajih, 24/10 : la variante « C », la chorégraphie gardée). Sur
 * `entree` (400 ms), elle se réduisait en 0,15 s à l'œil ; sur 700 ms, elle se réduit à vue. Dans la bulle,
 * où les visages sont déjà petits, la course garde `entree`.
 */
export const ORBIT_STAGE_FORWARD_MS = 700

/**
 * La durée d'une course, là où elle se joue : dans la visio vers Sam devant, `ORBIT_STAGE_FORWARD_MS` ;
 * sinon (le retour vers le masteur, la bulle), `entree`. Une seule règle pour ce qui attend l'arrivée : le
 * tandem de la visio, la bascule visio ↔ bulle, le voile de Sam envoyé.
 */
export function orbitDurationMs(place: "stage" | "bubble", samLeads: boolean, entree: number): number {
    return place === "stage" && samLeads ? ORBIT_STAGE_FORWARD_MS : entree
}

/**
 * Vers Sam devant : la part du chemin où la vidéo pleine du masteur commence à se fondre dans son visage rond
 * (80 % du chemin, vers 370 ms sur 700) ; avant, elle se voit se réduire jusqu'à lui.
 */
export const ORBIT_FORWARD_FADE_FROM = 0.8

/**
 * Le fondu de la vidéo pleine du masteur dans son visage rond, lu sur la phase de l'orbite (0 : Sam devant,
 * 1 : le masteur devant ; [0, 1) va vers le masteur, [1, 2) vers Sam) : au retour, il se fait à mi-course ;
 * vers Sam, à la fin (`ORBIT_FORWARD_FADE_FROM`). Lu sur la phase plutôt que sur le recul, il ne saute pas
 * quand un relais rebrousse chemin : une phase donnée a toujours le même fondu, quel que soit le sens.
 */
export function tutorFade(): { inputRange: number[]; video: number[]; round: number[] } {
    return {
        inputRange: [0, 0.5, 1 + ORBIT_FORWARD_FADE_FROM, 2],
        video: [0, 1, 1, 0],
        round: [1, 0, 0, 1],
    }
}

/** La courbe d'une course : vers Sam devant, celle qui se voit partir ; vers le masteur, celle de la marque. */
export function orbitEasing(samLeads: boolean, brand: OrbitCurve): OrbitCurve {
    return samLeads ? ORBIT_FORWARD_CURVE : brand
}

/** La course au repos : Sam devant sur un pair, l'autre sur un impair (positifs, même en rebroussant). */
export function restingTurn(samLeads: boolean): number {
    return samLeads ? 2 : 3
}

/** La course ramenée à un tour : [0, 1) Sam recule, [1, 2) Sam revient. */
export function orbitPhase(turn: number): number {
    return ((turn % 2) + 2) % 2
}

/** Sam mène à cet arrêt (un entier de la course). */
export function samLeadsAt(turn: number): boolean {
    return Math.round(orbitPhase(turn)) % 2 === 0
}

/** Qui se peint devant à cet instant : l'échange se fait à mi-course, pas au départ. */
export function samInFrontAt(turn: number): boolean {
    const phase = orbitPhase(turn)
    return phase < 0.5 || phase >= 1.5
}

/**
 * La course quand le relais est lu : inchangée s'il mène déjà ; sinon un demi-tour de plus, dans le
 * même sens ; si la course d'avant n'était pas finie (`running`), elle rebrousse chemin.
 */
export function relayCourse(course: OrbitCourse, samLeads: boolean, running: boolean): OrbitCourse {
    if (samLeadsAt(course.to) === samLeads) return course
    return running ? { from: course.to, to: course.from } : { from: course.to, to: course.to + 1 }
}

/** Le point de la course où l'on est, à la part `fraction` (0 à 1) du relais qui mène à `samLeads`. */
export function courseAt(samLeads: boolean, fraction: number): number {
    const to = restingTurn(samLeads)
    return to - 1 + fraction
}

const lerp = (from: number, to: number, t: number) => from + (to - from) * t

/**
 * Où est un visage à la phase `phase` (0 à 2) : Sam (`isSam`) est devant à 0 et recule jusqu'à 1 ;
 * l'autre avance jusqu'à 1, puis recule jusqu'à 2. Entre deux places, un arc : à mi-course, celui qui
 * recule s'écarte de `swing`, celui qui avance de l'écart opposé — vus de face, deux visages qui
 * tournent autour de leur centre commun, comme un carrousel, et se croisent sans se recouvrir. Sur deux
 * relais, chaque visage passe d'un côté puis de l'autre : l'orbite se referme. Aux arrêts, la place
 * exacte.
 */
export function orbitPose(face: OrbitFace, phase: number, isSam: boolean, swing: OrbitPoint): OrbitPose {
    const firstHalf = phase < 1
    const t = firstHalf ? phase : phase - 1
    // Sam recule sur la première moitié ; l'autre, sur la seconde.
    const receding = firstHalf === isSam
    const from = receding ? face.front : face.back
    const to = receding ? face.back : face.front
    // Aux arrêts, la place exacte : pas de reste d'arrondi d'un sinus.
    const away = t === 0 || t === 1 ? 0 : Math.sin(Math.PI * t) * (receding ? 1 : -1)
    return {
        x: lerp(from.center.x, to.center.x, t) + away * swing.x - face.front.center.x,
        y: lerp(from.center.y, to.center.y, t) + away * swing.y - face.front.center.y,
        scale: lerp(from.scale, to.scale, t),
        back: receding ? t : 1 - t,
    }
}

/** La piste d'un visage sur un tour entier (0 à 2), échantillonnée pour `Animated.interpolate`. */
export function orbitTrack(
    face: OrbitFace,
    isSam: boolean,
    swing: OrbitPoint
): { inputRange: number[]; x: number[]; y: number[]; scale: number[]; back: number[] } {
    const inputRange: number[] = []
    const x: number[] = []
    const y: number[] = []
    const scale: number[] = []
    const back: number[] = []
    for (let step = 0; step <= 2 * ORBIT_STEPS; step++) {
        const phase = step / ORBIT_STEPS
        const pose = orbitPose(face, phase, isSam, swing)
        inputRange.push(phase)
        x.push(pose.x)
        y.push(pose.y)
        scale.push(pose.scale)
        back.push(pose.back)
    }
    return { inputRange, x, y, scale, back }
}
